import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccountCircle, Delete, ModeEdit, PersonAdd } from '@mui/icons-material';
import { Box, CircularProgress, Divider, SpeedDialAction } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ConfirmModal, InfoItem, ViewPage } from '/src/components';
import { useFilterStore, useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { User } from '/src/lib/models';
import { CRUDMode, DataModelType, UserRole, UserStatus } from '/src/lib/types';
import { getSpeedDialActionProps } from '/src/utils';

/**
 * View User Page
 */
export const ViewUserPage = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const filterStore = useFilterStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  /** Computed **/
  const user = userStore.currentUser;
  const isActive = user?.status === UserStatus.Active;

  const companies = useMemo(() => filterStore.companyListGet(), [filterStore._companyList]);
  const lenders = useMemo(() => {
    const l = filterStore.lenderListGet();
    if (Array.isArray(l)) {
      return l;
    } else if (l && l._id) {
      return [l];
    }
    return [];
  }, [filterStore._lenderList]);

  const userCompany = (userStore.isLender ? lenders : companies).find((company) => company._id === user?.company);

  /** Methods **/

  /**
   * Deactivates this User.
   *
   * @remarks
   * We don't ever remove Users from the system, just mark them as "inactive".
   *
   * @remarks
   * Redirects to the Users List afterwards.
   */
  const deactivate = async (targetUser?: User) => {
    try {
      if (targetUser) {
        if (targetUser?.company)
          if (typeof targetUser.company !== 'string') targetUser.company = targetUser.company._id;
        targetUser.status = UserStatus.Inactive;
        userStore.update(targetUser);
      } else if (user) {
        if (user?.company) if (typeof user.company !== 'string') user.company = user.company._id;
        user.status = UserStatus.Inactive;
        await userStore.update(user);
        toast.success(toastMessages.deactivateUser.success);
        navigate('/users');
      }
    } catch (err) {
      toast.error(toastMessages.deactivateUser.error);
    }
  };

  /**
   * Activates this User.
   *
   * @remarks
   * Redirects to the Users List afterwards.
   */
  const activate = async () => {
    try {
      if (!user) return;
      if (user?.company) if (typeof user.company !== 'string') user.company = user.company._id;
      user.status = UserStatus.Active;
      await userStore.update(user);
      toast.success(toastMessages.activateUser.success);
      navigate('/users');
    } catch (err) {
      toast.error(toastMessages.activateUser.error);
    }
  };

  /** Effects **/

  useEffect(() => {
    params.userId && userStore.get(params.userId);
  }, [params.userId]);

  /** Render **/
  return (
    <ViewPage
      modelName={user?.displayName ?? ''}
      modelType={DataModelType.User}
      icon={<AccountCircle className="md-icon material-icons" />}
      width="50%"
      textClassName="md-title"
      speedDialActions={[
        <SpeedDialAction
          key="edit-button"
          icon={<ModeEdit className="material-icons" />}
          slotProps={getSpeedDialActionProps(CRUDMode.Edit, 'Edit User')}
          onClick={() => navigate(`/users/${user?._id}/edit`)}
        />,
        <SpeedDialAction
          key="delete-button"
          icon={user && isActive ? <Delete className="material-icons" /> : <PersonAdd className="material-icons" />}
          slotProps={getSpeedDialActionProps(
            user && isActive ? CRUDMode.Remove : CRUDMode.Edit,
            `${user && isActive ? 'Deactivate' : 'Activate'} User`
          )}
          onClick={() => setShowConfirmModal(true)}
          className="md-warn"
        />,
      ]}
      modals={
        <ConfirmModal
          content={`Are you sure you want to ${user && isActive ? 'deactivate' : 'activate'} this User?`}
          open={showConfirmModal}
          setOpen={setShowConfirmModal}
          onOk={() => (user && isActive ? deactivate : activate)()}
          label={`Confirm User ${user && isActive ? 'Deactivation' : 'Activation'}`}
          okLabel={user && isActive ? 'Deactivate' : 'Activate'}
          cancelLabel="Cancel"
        />
      }
    >
      {user ? (
        <>
          <InfoItem title="Username" value={user?.username} />
          <InfoItem title="Display Name" value={user?.displayName} />
          <Divider />
          <InfoItem title="Email" value={user?.email} url={`mailto:${user?.email}`} />
          <Divider />
          <InfoItem title="User Role" value={user?.roles[0]} capitalize />
          {!user.roles.includes(UserRole.Admin) && (
            <InfoItem
              title="User Company"
              value={userCompany?.name}
              url={`/companies/${userCompany?._id}`}
              capitalize
            />
          )}
        </>
      ) : (
        <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
          <CircularProgress className="md-primary" md-mode="indeterminate" />
        </Box>
      )}
    </ViewPage>
  );
});
